"use strict";

import {
    createTitleBlock,
    createChartTableHeaderRow,
    createChartEntry,
    createLegendGroup,
} from "./forecast-plots-plotting-elements.js";
import { calculateMinMax, MARGINS } from "./forecast-plots-plotting-utils.js";
import * as d3 from "d3";
/**
 * Creates the forecast plot.
 *
 * @param {*} entries - data entries (see createEntries)
 * @param {*} inputs - specified chart options (e.g.
 *              chartWidth, showForecastLabels, etc)
 */
export function createChart(entries, inputs) {
    // Initialize the chart (delete any old charts)
    d3.selectAll("#chart > *").remove();
    const chart = d3.select("#chart");
    chart.append("defs");
    chart.attr("xmlns", "http://www.w3.org/2000/svg");

    // Set the chart width and font-family
    chart.attr("width", +inputs.chartWidth);
    chart.attr("font-family", "sans-serif");

    // Create the title block
    createTitleBlock(chart, inputs);

    // Create the chart header
    createChartTableHeaderRow(chart, inputs);

    // create the x-scale
    const scaleWidth = +inputs.chartWidth - MARGINS.left - MARGINS.right;
    const { min, max } = calculateMinMax(entries, inputs);
    const xScale = d3.scaleLinear().range([0, scaleWidth]).domain([min, max]).nice();

    // initialize the y-position and last station
    chart.attr("data-current-y", -10);
    chart.attr("data-last-station", null);

    // create the entry group
    const entryGroup = chart
        .append("g")
        .attr("id", "entry-group")
        .attr("transform", `translate(180, ${MARGINS.top + 20})`)
        .attr("font-size", "12px");

    // Initialize a gray 100% line if the scale includes 100
    let normalLine;
    if (100 >= min && 100 <= max) {
        normalLine = entryGroup
            .append("line")
            .attr("x1", xScale(100))
            .attr("x2", xScale(100))
            .attr("y1", -20)
            .attr("stroke", "#eee")
            .attr("stroke-width", 4);
    }

    // If there are no entries, display message
    if (!entries.length) {
        entryGroup
            .append("text")
            .text("No forecasts to display.")
            .attr("x", xScale((min + max) / 2))
            .attr("y", 0)
            .attr("text-anchor", "middle")
            .attr("font-weight", "bold");
    }

    // Draw entries
    entries.forEach((entry) => createChartEntry(chart, entry, inputs, xScale));

    // Get the new y position (with some padding)
    let currentY = +chart.attr("data-current-y") + 40;

    // draw some lines to bound the plot (also finish the normal line)
    if (normalLine) {
        normalLine.attr("y2", currentY - 20);
    }
    chart
        .append("line")
        .attr("x1", MARGINS.left + 0.5)
        .attr("x2", MARGINS.left + 0.5)
        .attr("y1", MARGINS.top)
        .attr("y2", MARGINS.top + currentY)
        .attr("stroke", "black")
        .attr("stroke-width", 1);
    chart
        .append("line")
        .attr("x1", scaleWidth + MARGINS.left + 0.5)
        .attr("x2", scaleWidth + MARGINS.left + 0.5)
        .attr("y1", MARGINS.top)
        .attr("y2", MARGINS.top + currentY)
        .attr("stroke", "black")
        .attr("stroke-width", 1);

    // Add the x-axis with a axis label
    const xAxis = d3.axisBottom(xScale).tickFormat((i) => `${i}%`);
    const xAxisGroup = chart
        .append("g")
        .attr("class", "x axis")
        .attr("transform", `translate(${MARGINS.left}, ${currentY + MARGINS.top})`)
        .attr("id", "x-axis")
        .call(xAxis)
        .attr("font-size", "12px")
        .attr("text-anchor", "middle");
    xAxisGroup
        .append("text")
        .attr("transform", `translate(${scaleWidth / 2}, 35)`)
        .attr("text-anchor", "middle")
        .attr("fill", "#000")
        .text("Percent of " + (inputs.normalType == "AVG" ? "Average" : "Median") + " (1991-2020)");

    // create the legend
    createLegendGroup(chart);
    d3.select("#legend-group").attr(
        "transform",
        `translate(${+inputs.chartWidth / 2}, ${60 + currentY + MARGINS.top})`,
    );

    // finalize the chart by setting a background and setting the final height
    chart.attr("height", currentY + MARGINS.top + MARGINS.bottom);
    const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    bg.setAttribute("width", chart.attr("width"));
    bg.setAttribute("height", chart.attr("height"));
    bg.setAttribute("x", 0);
    bg.setAttribute("y", 0);
    bg.setAttribute("fill", "#fff");
    bg.setAttribute("stroke-width", 1);
    bg.setAttribute("stroke", "black");
    chart.node().prepend(bg);
}
